import React, { useContext, useEffect, useRef, useState } from 'react';
import classnames from 'clsx';
import { formatClassNames } from '@wix/editor-elements-common-utils';
import { usePopper } from '../../../../../../providers/usePopper/usePopper';
import { useClickOutside } from '../../../../../../providers/useClickOutside/useClickOutside';
import styles from '../button.scss';
import StylesContext from '../../../StylesContext';
import { TestIds } from '../../../../constants';
import semanticClassNames from '../../../../RichTextBox.semanticClassNames';
import AlignButton from './AlignButton';
import type { AlignButtonType } from './common';
import {
  alignmentButtons,
  getActiveAlignment,
  preventBubblingUp,
  getStateWithNewTextAlign,
  alignTypes,
} from './common';
import type { ExtendedToolbarChildrenProps } from '../../types';

export default (props: ExtendedToolbarChildrenProps) => {
  const [showOptions, setShowOptions] = useState(false);
  const [shouldFocus, setShouldFocus] = useState(false);
  const leftAlignRef = useRef<HTMLButtonElement>(null);

  const {
    ref: popperSourceElem,
    setRef: setPopperSourceElem,
    setPopper: setPopperTargetElem,
    styles: stylesPopper,
    attributes,
  } = usePopper<HTMLButtonElement>({
    placement: 'bottom',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, -25],
        },
      },
    ],
  });

  const stylesContext = useContext(StylesContext);
  const { setEditorState, getEditorState, rootId } = props;

  const hideOptions = () => {
    setShowOptions(false);
  };

  useClickOutside([popperSourceElem], hideOptions);

  const changeTextAlign = (
    e: React.MouseEvent<HTMLOrSVGElement>,
    newAlignment: AlignButtonType,
  ) => {
    preventBubblingUp(e);

    const newState = getStateWithNewTextAlign(newAlignment, getEditorState());
    hideOptions();
    setEditorState(newState);
  };

  const onClickHandler = (event: React.MouseEvent<any>) => {
    preventBubblingUp(event);
    if (event.currentTarget === popperSourceElem) {
      setShowOptions(!showOptions);
      setShouldFocus(true);
    }
  };

  useEffect(() => {
    if (shouldFocus) {
      leftAlignRef.current?.parentElement?.focus();
      setShouldFocus(false);
    }
  }, [shouldFocus]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      setShowOptions(false);
      popperSourceElem?.focus();
    }
  };

  const activeAlignment = getActiveAlignment(getEditorState());

  return (
    <div className={styles.buttonContainer} onMouseDown={preventBubblingUp}>
      <button
        type="button"
        ref={setPopperSourceElem}
        className={classnames(styles.button, {
          [styles.hovered]: stylesContext.hovered,
          [styles.focused]: stylesContext.focused,
          [styles.error]: stylesContext.error,
        })}
        onClick={onClickHandler}
        data-testid={TestIds.alignButton}
        aria-haspopup="true"
        aria-expanded={showOptions}
        aria-controls={`alignMenu_${rootId}`}
      >
        <div
          className={classnames(
            styles.buttonContent,
            formatClassNames(semanticClassNames.icons),
          )}
        >
          <AlignButton tabIndex={-1}>
            {alignmentButtons[activeAlignment]}
          </AlignButton>
        </div>
      </button>
      <div
        ref={node => setPopperTargetElem(node)} // directly passing setPopperTargetElem doesn't work
        style={{
          ...stylesPopper.popper,
          visibility: showOptions ? 'visible' : 'hidden',
          zIndex: '1',
        }}
        {...attributes.popper}
        onClick={onClickHandler}
        role="menu"
        id={`alignMenu_${rootId}`}
      >
        <div
          className={classnames(styles.menuContainer, {
            [styles.hovered]: stylesContext.hovered,
            [styles.focused]: stylesContext.focused,
            [styles.error]: stylesContext.error,
          })}
          onKeyDown={onKeyDown}
        >
          {alignTypes.map(alignType => {
            return (
              <button
                onClick={e => changeTextAlign(e, alignType)}
                key={alignType}
                data-testid={`${TestIds.alignButton}-${alignType}`}
                ref={alignType === 'left' ? leftAlignRef : undefined}
              >
                <AlignButton
                  selected={activeAlignment === alignType}
                  onClick={e => changeTextAlign(e, alignType)}
                >
                  {alignmentButtons[alignType]}
                </AlignButton>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
